import { ShoppingCart, Check, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCart } from "@/lib/cart";
import type { Product } from "@shared/schema";
import { Link } from "wouter";
import { getSubscriptionAccessMessage } from "@/lib/subscription-copy";

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const { items, addItem } = useCart();
  const isInCart = items.some((item) => item.product.id === product.id);

  const saleType: string = (product as any).saleType ?? "purchase";
  const canPurchase = saleType === "purchase" || saleType === "both";
  const canSubscribe = saleType === "subscribe" || saleType === "both";
  const subscriptionAccessMessage = getSubscriptionAccessMessage(product.categoryId);

  return (
    <Card className="overflow-hidden group" data-testid={`card-product-${product.id}`}>
      <Link href={`/product/${product.slug}`}>
        <div className="relative overflow-hidden cursor-pointer">
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
              data-testid={`img-product-${product.id}`}
            />
          )}
        </div>
      </Link>
      <CardContent className="p-4">
        {product.isFeatured && (
          <Badge className="mb-2" data-testid={`badge-featured-${product.id}`}>
            Featured
          </Badge>
        )}
        <Link href={`/product/${product.slug}`}>
          <h3 className="font-semibold mb-1 cursor-pointer hover:text-primary transition-colors" data-testid={`text-product-name-${product.id}`}>
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2" data-testid={`text-product-desc-${product.id}`}>
          {product.description}
        </p>

        {product.features && product.features.length > 0 && (
          <TooltipProvider delayDuration={200}>
            <div className="flex flex-col gap-1 mb-3">
              {product.features.slice(0, 3).map((feature) => (
                <Tooltip key={feature}>
                  <TooltipTrigger asChild>
                    <div className="max-w-full truncate">
                      <Badge variant="secondary" className="text-xs max-w-full truncate cursor-default">
                        {feature}
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs bg-white text-black border-white">
                    <p className="text-xs">{feature}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        )}

        {/* Subscribe-only */}
        {!canPurchase && canSubscribe && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground italic">Subscription required</span>
              <Link href="/pricing">
                <Button size="sm" className="gap-1" data-testid={`button-subscribe-${product.id}`}>
                  <Zap className="w-3.5 h-3.5" />
                  Subscribe
                </Button>
              </Link>
            </div>
            <p className="rounded-md border border-amber-400/60 bg-amber-400/10 px-2.5 py-1.5 text-center text-xs font-semibold leading-snug text-amber-300 transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-400/20 hover:text-amber-200 hover:shadow-[0_4px_14px_rgba(251,191,36,0.2)]">
              {subscriptionAccessMessage}
            </p>
          </div>
        )}

        {/* Purchase-only (original behaviour) */}
        {canPurchase && !canSubscribe && (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xl font-bold text-primary" data-testid={`text-price-${product.id}`}>
              ${Number(product.price).toFixed(2)}
            </span>
            <Button
              onClick={() => addItem(product)}
              disabled={isInCart}
              variant={isInCart ? "secondary" : "default"}
              data-testid={`button-add-to-cart-${product.id}`}
            >
              {isInCart ? (
                <><Check className="w-4 h-4 mr-1" />In Cart</>
              ) : (
                <><ShoppingCart className="w-4 h-4 mr-1" />Add to Cart</>
              )}
            </Button>
          </div>
        )}

        {/* Both */}
        {canPurchase && canSubscribe && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xl font-bold text-primary" data-testid={`text-price-${product.id}`}>
                ${Number(product.price).toFixed(2)}
              </span>
              <Button
                onClick={() => addItem(product)}
                disabled={isInCart}
                variant={isInCart ? "secondary" : "default"}
                data-testid={`button-add-to-cart-${product.id}`}
              >
                {isInCart ? (
                  <><Check className="w-4 h-4 mr-1" />In Cart</>
                ) : (
                  <><ShoppingCart className="w-4 h-4 mr-1" />Add to Cart</>
                )}
              </Button>
            </div>
            <Link href="/pricing">
              <Button size="sm" variant="outline" className="w-full gap-1 text-xs" data-testid={`button-subscribe-${product.id}`}>
                <Zap className="w-3 h-3" />
                Or Subscribe
              </Button>
            </Link>
            <p className="rounded-md border border-amber-400/60 bg-amber-400/10 px-2.5 py-1.5 text-center text-xs font-semibold leading-snug text-amber-300 transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-400/20 hover:text-amber-200 hover:shadow-[0_4px_14px_rgba(251,191,36,0.2)]">
              {subscriptionAccessMessage}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
